/**
 * The manage secret lives in the URL fragment.
 *
 * A fragment is never sent in an HTTP request and never appears in a `Referer` header,
 * a server log or a proxy trace. The client reads it here and hands it to the API in an
 * Authorization header — the only place it is ever transmitted.
 *
 * It is deliberately never written to storage: the organiser's link is the credential,
 * which is what the share screen tells them in plain words.
 */

const MANAGE_KEY = 'manage';
const NEW_ROOM_KEY = 'new';

function fragmentParams(): URLSearchParams {
  const raw = window.location.hash.startsWith('#')
    ? window.location.hash.slice(1)
    : window.location.hash;
  return new URLSearchParams(raw);
}

export function readManageSecret(): string | null {
  const value = fragmentParams().get(MANAGE_KEY);
  if (!value) return null;
  // Same shape the API validates; anything else is treated as absent.
  return /^[A-Za-z0-9_-]{20,128}$/.test(value) ? value : null;
}

/** True immediately after creation, when the share screen should lead. */
export function isFreshlyCreated(): boolean {
  return fragmentParams().get(NEW_ROOM_KEY) === '1';
}

export function buildManageFragment(secret: string, fresh: boolean): string {
  const params = new URLSearchParams();
  params.set(MANAGE_KEY, secret);
  if (fresh) params.set(NEW_ROOM_KEY, '1');
  return `#${params.toString()}`;
}

/**
 * Drop the "just created" marker while keeping the manage secret, so a reload after the
 * share screen goes straight to the room. Uses replaceState: no navigation, no history
 * entry, and the fragment never becomes a back-button trap.
 */
export function clearFreshlyCreatedFlag(): void {
  const secret = readManageSecret();
  const hash = secret ? buildManageFragment(secret, false) : '';
  window.history.replaceState(null, '', `${window.location.pathname}${hash}`);
}

/** The room id from `/r/<id>`. */
export function roomIdFromPath(): string | null {
  const match = /^\/r\/([A-Za-z0-9_-]+)\/?$/.exec(window.location.pathname);
  return match?.[1] ?? null;
}
